import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Wand2,
  Plus,
  Trash2,
  Save,
  Loader2,
  BookOpen,
  Users,
  Clock,
  Play,
  Settings2,
  AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AutoAssignmentRules = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger_type: 'new_employee',
    trigger_conditions: {},
    course_ids: [],
    due_days: 30
  });

  const triggerTypes = [
    { id: 'new_employee', name: 'New Employee Joins', description: 'Assign when a new employee is added' },
    { id: 'department_change', name: 'Department Assignment', description: 'Assign to employees in specific departments' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, coursesRes] = await Promise.all([
        axios.get(`${API}/auto-assignment-rules`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true })
      ]);
      setRules(rulesRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load auto-assignment rules");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async () => {
    if (!newRule.name.trim()) {
      toast.error("Please enter a rule name");
      return;
    }
    if (newRule.course_ids.length === 0) {
      toast.error("Please select at least one course");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/auto-assignment-rules`, newRule, { withCredentials: true });
      setRules([...rules, response.data]);
      toast.success("Auto-assignment rule created!");
      setShowCreateDialog(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create rule");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (ruleId, isActive) => {
    try {
      await axios.put(`${API}/auto-assignment-rules/${ruleId}/toggle?is_active=${isActive}`, {}, { withCredentials: true });
      setRules(rules.map(r => r.rule_id === ruleId ? { ...r, is_active: isActive } : r));
      toast.success(`Rule ${isActive ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to toggle rule");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm("Are you sure you want to delete this rule?")) return;
    
    try {
      await axios.delete(`${API}/auto-assignment-rules/${ruleId}`, { withCredentials: true });
      setRules(rules.filter(r => r.rule_id !== ruleId));
      toast.success("Rule deleted");
    } catch (error) {
      toast.error("Failed to delete rule");
    }
  };

  const handleCourseToggle = (courseId) => {
    setNewRule(prev => ({
      ...prev,
      course_ids: prev.course_ids.includes(courseId)
        ? prev.course_ids.filter(id => id !== courseId)
        : [...prev.course_ids, courseId]
    }));
  };

  const resetForm = () => {
    setNewRule({
      name: '',
      description: '',
      trigger_type: 'new_employee',
      trigger_conditions: {},
      course_ids: [],
      due_days: 30
    });
  };

  const getCourseTitle = (courseId) => {
    const course = courses.find(c => c.course_id === courseId);
    return course?.title || courseId;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="auto-assignment-rules-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
                Auto-Assignment Rules
              </h1>
              <p className="text-muted-foreground mt-1">
                Automatically assign training courses to employees based on rules.
              </p>
            </div>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-rule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Auto-Assignment Rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="rule-name">Rule Name</Label>
                  <Input
                    id="rule-name"
                    value={newRule.name}
                    onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                    placeholder="e.g., New Hire Onboarding"
                    data-testid="rule-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="rule-description">Description (optional)</Label>
                  <Textarea
                    id="rule-description"
                    value={newRule.description}
                    onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                    placeholder="Describe what this rule does..."
                  />
                </div>
                
                <div>
                  <Label>Trigger Type</Label>
                  <Select 
                    value={newRule.trigger_type} 
                    onValueChange={(v) => setNewRule({ ...newRule, trigger_type: v, trigger_conditions: {} })}
                  >
                    <SelectTrigger data-testid="trigger-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {triggerTypes.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          <div>
                            <span className="font-medium">{t.name}</span>
                            <p className="text-xs text-muted-foreground">{t.description}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {newRule.trigger_type === 'department_change' && (
                  <div>
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={newRule.trigger_conditions.department || ''}
                      onChange={(e) => setNewRule({
                        ...newRule,
                        trigger_conditions: { ...newRule.trigger_conditions, department: e.target.value }
                      })}
                      placeholder="e.g., Warehouse, Operations"
                    />
                  </div>
                )}
                
                <div>
                  <Label>Due in (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={newRule.due_days}
                    onChange={(e) => setNewRule({ ...newRule, due_days: parseInt(e.target.value) || 30 })}
                    data-testid="due-days-input"
                  />
                </div>
                
                <div>
                  <Label className="mb-2 block">Courses to Assign</Label>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2">
                    {courses.map(course => (
                      <div key={course.course_id} className="flex items-center gap-2">
                        <Checkbox
                          id={course.course_id}
                          checked={newRule.course_ids.includes(course.course_id)}
                          onCheckedChange={() => handleCourseToggle(course.course_id)}
                        />
                        <Label htmlFor={course.course_id} className="text-sm cursor-pointer flex-1">
                          {course.title}
                        </Label>
                        <Badge variant="secondary" className="text-xs">
                          {course.duration_minutes}min
                        </Badge>
                      </div>
                    ))}
                    {courses.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No courses available
                      </p>
                    )}
                  </div>
                </div>
                
                <Button onClick={handleCreateRule} disabled={saving} className="w-full" data-testid="save-rule-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Create Rule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">How Auto-Assignment Works</p>
              <p className="text-sm text-blue-700 mt-1">
                When a rule is active and its trigger condition is met (e.g., new employee joins), 
                the selected courses are automatically assigned to that employee with the specified due date.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Rules List */}
        {rules.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Wand2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-semibold text-lg mb-2">No Rules Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first auto-assignment rule to automate training assignments.
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Rule
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {rules.map((rule) => (
              <Card key={rule.rule_id} data-testid={`rule-${rule.rule_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{rule.name}</h3>
                        <Badge className={rule.is_active ? 'badge-success' : 'badge-neutral'}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      {rule.description && (
                        <p className="text-sm text-muted-foreground mt-1">{rule.description}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-4 mt-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Play className="w-4 h-4 text-muted-foreground" />
                          <span>
                            Trigger: <strong>{triggerTypes.find(t => t.id === rule.trigger_type)?.name || rule.trigger_type}</strong>
                          </span>
                        </div>
                        {rule.trigger_conditions?.department && (
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span>
                              Department: <strong>{rule.trigger_conditions.department}</strong>
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>
                            Due in: <strong>{rule.due_days} days</strong>
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <p className="text-sm text-muted-foreground mb-2">Courses:</p>
                        <div className="flex flex-wrap gap-2">
                          {rule.course_ids.map(courseId => (
                            <Badge key={courseId} variant="outline" className="flex items-center gap-1">
                              <BookOpen className="w-3 h-3" />
                              {getCourseTitle(courseId)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {rule.last_triggered && (
                        <p className="text-xs text-muted-foreground mt-3">
                          Last triggered: {new Date(rule.last_triggered).toLocaleString()}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3 ml-4">
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={(checked) => handleToggleRule(rule.rule_id, checked)}
                        data-testid={`toggle-${rule.rule_id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRule(rule.rule_id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AutoAssignmentRules;
